import type { IconName } from '@raiadrogasil/pulso-icons'
import { Children, cloneElement, forwardRef, isValidElement } from 'react'
import { Icon } from '~/components/icon'
import {
  actionsContainerVariants,
  actionsVariants,
  checkboxIconNoChildrenVariants,
  checkboxIconVariants,
  checkboxVariants,
  helperTextNoChildrenVariants,
  helperTextVariants,
  noChildrenVariants,
  parentVariants,
} from './checkbox.styles'
import { CheckboxLabel } from './components/checkbox-label'
import { CheckboxHelperText } from './components/helper-text'
import type { TCheckboxProps, TCheckboxSizes } from './types'

const DISPLAY_NAME = 'Checkbox'

const mapSizeToIconSize = (size: TCheckboxSizes) => {
  const sizeMap: Record<
    TCheckboxSizes,
    'tiny' | 'extra-small' | 'small' | 'medium'
  > = {
    md: 'tiny',
    ml: 'small',
    lg: 'medium',
    xl: 'medium',
  }

  return sizeMap[size] || 'medium'
}

const CheckboxIconCheck = ({
  symbol,
  size = 'md',
  disabled,
  readonly,
  hasLabelText,
}: {
  symbol: IconName
  size?: TCheckboxProps['size']
  disabled?: boolean
  readonly?: boolean
  hasLabelText?: boolean
}) => {
  const iconSize = mapSizeToIconSize(size)

  const iconClasses = hasLabelText
    ? checkboxIconVariants({ size })
    : checkboxIconNoChildrenVariants({ size })

  const iconColor = disabled
    ? 'colorTextNeutralDisabled'
    : readonly
      ? 'colorTextNeutralReadonly'
      : 'colorTextNeutralDefault'

  return (
    <div className={iconClasses}>
      <Icon symbol={symbol} size={iconSize} color={iconColor} />
    </div>
  )
}

const CheckboxActions = ({
  children,
  size,
  disabled,
  readonly,
  error,
}: {
  children: React.ReactNode
  size?: TCheckboxProps['size']
  disabled?: boolean
  readonly?: boolean
  error?: boolean
}) => {
  const hasChildren = Boolean(
    Children.toArray(children).find(
      child =>
        isValidElement(child) &&
        child.type === CheckboxLabel &&
        child.props.children
    )
  )

  const containerClassName = hasChildren
    ? actionsContainerVariants({
        size,
        hasDisabled: disabled,
        hasReadonly: readonly,
        hasError: !!error,
      })
    : noChildrenVariants({
        size,
        hasDisabled: disabled,
        hasReadonly: readonly,
        hasError: !!error,
      })

  return (
    <div
      className={actionsVariants({
        size,
        hasError: !!error,
      })}
    >
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation> */}
      <div className={containerClassName} tabIndex={0}>
        {children}
      </div>
    </div>
  )
}

const CheckboxRoot = forwardRef<HTMLDivElement, TCheckboxProps>(
  (props, ref) => {
    const {
      size = 'md',
      checked = false,
      indeterminate = false,
      onClick,
      error,
      disabled,
      readonly,
      children,
      feedbackMessage,
      hasFeedbackMessage = true,
      icon,
      ...rest
    } = props

    const currentStateSymbol: IconName = indeterminate
      ? 'rdicon-checkbox-indeterminate'
      : checked
        ? 'rdicon-checkbox-checked'
        : 'rdicon-checkbox-unchecked'

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation()

      if (!(disabled || readonly) && typeof onClick === 'function') {
        onClick(!checked)
      }
    }

    const renderInternalLabel = Children.toArray(children).find(
      child => isValidElement(child) && child.type === CheckboxLabel
    ) as React.ReactElement | undefined

    const renderInternalHelperText = Children.toArray(children).find(
      child => isValidElement(child) && child.type === CheckboxHelperText
    ) as React.ReactElement | undefined

    const renderParent = Children.toArray(children).find(
      child => isValidElement(child) && child.type === CheckboxParent
    ) as React.ReactElement | undefined

    const filteredProps = rest as TCheckboxProps
    const { feedbackMessage: _, ...propsForDom } = filteredProps

    const shouldDisplayFeedback =
      error || (hasFeedbackMessage && feedbackMessage)

    return (
      <div
        ref={ref}
        {...propsForDom}
        role="checkbox"
        aria-checked={checked || indeterminate}
        aria-disabled={disabled || readonly}
        onClick={handleClick}
        className={checkboxVariants({
          size,
          hasError: !!error,
          hasDisabled: !!disabled,
          hasReadonly: !!readonly,
        })}
      >
        <CheckboxActions
          size={size}
          disabled={disabled}
          readonly={readonly}
          error={error}
        >
          <CheckboxIconCheck
            symbol={currentStateSymbol}
            size={size}
            disabled={disabled}
            readonly={readonly}
            hasLabelText={!!renderInternalLabel?.props.children}
          />
          {renderInternalLabel &&
            cloneElement(renderInternalLabel, {
              size,
              disabled: disabled,
              readonly: readonly,
              error,
            })}
        </CheckboxActions>
        {(renderInternalHelperText || shouldDisplayFeedback) &&
          renderInternalLabel && (
            <div
              className={
                renderInternalHelperText?.props.children ||
                shouldDisplayFeedback
                  ? helperTextVariants({ size, hasError: !!error })
                  : helperTextNoChildrenVariants({ size, hasError: !!error })
              }
            >
              {cloneElement(
                renderInternalHelperText || (
                  <CheckboxHelperText
                    icon={error ? 'rdicon-warning-circle' : icon}
                    isError={error}
                  >
                    {feedbackMessage}
                  </CheckboxHelperText>
                ),
                { size }
              )}
            </div>
          )}
        {renderParent &&
          cloneElement(renderParent, {
            size,
            disabled: disabled || readonly,
            error,
          })}
      </div>
    )
  }
)

const CheckboxParent = ({
  children,
  size = 'md',
}: {
  children: React.ReactNode
  size?: TCheckboxProps['size']
}) => <div className={parentVariants({ size })}>{children}</div>

CheckboxRoot.displayName = DISPLAY_NAME

export const Checkbox = {
  Root: CheckboxRoot,
  Label: CheckboxLabel,
  HelperText: CheckboxHelperText,
  Parent: CheckboxParent,
  Actions: CheckboxActions,
  IconCheck: CheckboxIconCheck,
}
